import React, { useState, useEffect } from 'react';
import { API_URL } from '../utils/constants';

const RevenueTracker = ({ session, onClose, showToast }) => {
  const [integrations, setIntegrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [connectingProvider, setConnectingProvider] = useState(null);
  const [showManualForm, setShowManualForm] = useState(false);
  const [manualRevenue, setManualRevenue] = useState({
    amount: '',
    productName: '',
    source: 'manual',
    occurredAt: new Date().toISOString().slice(0, 16)
  });

  const notify = (msg, type) => {
    if (showToast) showToast(msg, type);
  };

  const providers = [
    // { id: 'stripe', name: 'Stripe', icon: '💳', color: '#635BFF', available: true },
    // { id: 'gumroad', name: 'Gumroad', icon: '🛍️', color: '#FF90E8', available: true },
    { id: 'lemonsqueezy', name: 'Lemon Squeezy', icon: '🍋', color: '#FFC233', available: false },
    { id: 'paypal', name: 'PayPal', icon: '💰', color: '#0070BA', available: false }
  ];

  useEffect(() => {
    loadIntegrations();
  }, [session]); // eslint-disable-line

  const loadIntegrations = async () => {
    if (!session) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/integrations`, {
        headers: { 'Authorization': `Bearer ${session.access_token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setIntegrations(data.integrations || []);
      }
    } catch (err) {
      console.error('Failed to load integrations:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleConnect = async (providerId) => {
    if (!session) return;
    setConnectingProvider(providerId);

    try {
      const res = await fetch(`${API_URL}/api/integrations/connect`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ provider: providerId })
      });

      if (res.ok) {
        const data = await res.json();
        if (data.authUrl) {
          window.location.href = data.authUrl;
        } else if (data.success) {
          await loadIntegrations();
          notify(`${providerId} connected successfully!`, 'success');
        }
      } else {
        const data = await res.json().catch(() => ({}));
        notify(data.error || 'Failed to connect integration', 'error');
      }
    } catch (err) {
      console.error('Connection failed:', err);
      notify('Failed to connect. Please try again.', 'error');
    } finally {
      setConnectingProvider(null);
    }
  };

  const handleDisconnect = async (providerId) => {
    if (!window.confirm(`Disconnect ${providerId}? Revenue tracking will stop.`)) return;

    try {
      const res = await fetch(`${API_URL}/api/integrations/${providerId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${session.access_token}` }
      });

      if (res.ok) {
        await loadIntegrations();
        notify(`${providerId} disconnected`, 'info');
      }
    } catch (err) {
      console.error('Disconnect failed:', err);
    }
  };

  const handleManualSubmit = async () => {
    if (!session || !manualRevenue.amount || !manualRevenue.productName) {
      notify('Please fill in amount and product name', 'error');
      return;
    }

    try {
      const res = await fetch(`${API_URL}/api/revenue-events`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          amount: parseFloat(manualRevenue.amount),
          productName: manualRevenue.productName,
          source: 'manual',
          occurredAt: new Date(manualRevenue.occurredAt)
        })
      });

      if (res.ok) {
        setShowManualForm(false);
        setManualRevenue({
          amount: '',
          productName: '',
          source: 'manual',
          occurredAt: new Date().toISOString().slice(0, 16)
        });
        notify('Revenue recorded!', 'success');
      } else {
        notify('Failed to record revenue', 'error');
      }
    } catch (err) {
      console.error('Manual revenue submit failed:', err);
      notify('Failed to record revenue', 'error');
    }
  };

  const isConnected = (providerId) => {
    return integrations.some(i => i.provider === providerId && i.is_active);
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex',
      alignItems: 'center', justifyContent: 'center', zIndex: 1000, backdropFilter: 'blur(8px)',
      animation: 'fadeIn 0.2s ease', padding: '20px'
    }} onClick={onClose}>
      <div style={{
        background: 'var(--bg-card)', borderRadius: '20px', padding: '32px', width: '100%',
        maxWidth: '600px', boxShadow: 'var(--shadow-lg)', border: '1px solid var(--border-color)',
        animation: 'modalIn 0.3s cubic-bezier(0.16, 1, 0.3, 1)', maxHeight: '90vh', overflow: 'auto'
      }} onClick={e => e.stopPropagation()}>

        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <div style={{ fontSize: '40px', marginBottom: '12px' }}>💰</div>
          <h2 style={{ fontSize: '24px', fontWeight: '700', color: 'var(--text-primary)', marginBottom: '8px' }}>
            Revenue Tracking
          </h2>
          <p style={{ color: 'var(--text-secondary)', fontSize: '14px' }}>
            Connect your payment providers to automatically track revenue
          </p>
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px 20px' }}>
            <div style={{
              width: '40px', height: '40px', border: '3px solid var(--border-color)',
              borderTopColor: 'var(--accent-primary)', borderRadius: '50%',
              animation: 'spin 1s linear infinite', margin: '0 auto 16px'
            }} />
            <p style={{ color: 'var(--text-secondary)' }}>Loading integrations...</p>
          </div>
        ) : (
          <>
            {/* Integrations Grid */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px', marginBottom: '24px' }}>
              {providers.map(provider => {
                const connected = isConnected(provider.id);
                const connecting = connectingProvider === provider.id;

                return (
                  <div
                    key={provider.id}
                    style={{
                      padding: '20px', borderRadius: '14px',
                      border: `2px solid ${connected ? provider.color : 'var(--border-color)'}`,
                      background: connected ? `${provider.color}10` : 'var(--bg-secondary)',
                      opacity: provider.available ? 1 : 0.6
                    }}
                  >
                    <div style={{ fontSize: '36px', marginBottom: '10px', textAlign: 'center' }}>
                      {provider.icon}
                    </div>
                    <div style={{
                      fontSize: '16px', fontWeight: '600', color: 'var(--text-primary)',
                      textAlign: 'center', marginBottom: '12px'
                    }}>
                      {provider.name}
                    </div>

                    {!provider.available ? (
                      <div style={{
                        padding: '8px 12px', borderRadius: '8px',
                        background: 'var(--bg-tertiary)', textAlign: 'center',
                        fontSize: '12px', color: 'var(--text-muted)'
                      }}>
                        Coming Soon
                      </div>
                    ) : connected ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        <div style={{
                          padding: '8px 12px', borderRadius: '8px',
                          background: `${provider.color}20`, textAlign: 'center',
                          fontSize: '13px', color: provider.color, fontWeight: '600'
                        }}>
                          ✓ Connected
                        </div>
                        <button
                          onClick={() => handleDisconnect(provider.id)}
                          style={{
                            padding: '8px 12px', borderRadius: '8px',
                            border: '1px solid var(--border-color)', background: 'transparent',
                            color: 'var(--text-secondary)', fontSize: '12px', cursor: 'pointer',
                            fontFamily: 'inherit'
                          }}
                        >
                          Disconnect
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => handleConnect(provider.id)}
                        disabled={connecting}
                        style={{
                          width: '100%', padding: '10px 12px', borderRadius: '8px', border: 'none',
                          background: provider.color, color: 'white',
                          fontSize: '13px', fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit',
                          opacity: connecting ? 0.7 : 1
                        }}
                      >
                        {connecting ? 'Connecting...' : 'Connect'}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Manual Entry Option */}
            <div style={{
              padding: '20px', borderRadius: '14px', marginBottom: '24px',
              background: 'var(--bg-secondary)', border: '2px dashed var(--border-color)'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                <div style={{ fontSize: '24px' }}>📝</div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '15px', fontWeight: '600', color: 'var(--text-primary)' }}>
                    Manual Entry
                  </div>
                  <div style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
                    Record revenue that doesn't go through payment providers
                  </div>
                </div>
              </div>

              {!showManualForm ? (
                <button
                  onClick={() => setShowManualForm(true)}
                  style={{
                    width: '100%', padding: '10px 12px', borderRadius: '8px',
                    border: '2px solid var(--accent-primary)', background: 'transparent',
                    color: 'var(--accent-primary)', fontSize: '13px', fontWeight: '600',
                    cursor: 'pointer', fontFamily: 'inherit'
                  }}
                >
                  + Add Manual Revenue
                </button>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '12px' }}>
                  <input
                    type="number"
                    placeholder="Amount (USD)"
                    value={manualRevenue.amount}
                    onChange={e => setManualRevenue({ ...manualRevenue, amount: e.target.value })}
                    min="0"
                    step="0.01"
                    style={{
                      padding: '12px 14px', borderRadius: '8px',
                      border: '2px solid var(--border-color)', background: 'var(--bg-tertiary)',
                      color: 'var(--text-primary)', fontSize: '14px', fontFamily: 'inherit',
                      outline: 'none'
                    }}
                  />
                  <input
                    type="text"
                    placeholder="Product/Service Name"
                    value={manualRevenue.productName}
                    onChange={e => setManualRevenue({ ...manualRevenue, productName: e.target.value })}
                    style={{
                      padding: '12px 14px', borderRadius: '8px',
                      border: '2px solid var(--border-color)', background: 'var(--bg-tertiary)',
                      color: 'var(--text-primary)', fontSize: '14px', fontFamily: 'inherit',
                      outline: 'none'
                    }}
                  />
                  <input
                    type="datetime-local"
                    value={manualRevenue.occurredAt}
                    onChange={e => setManualRevenue({ ...manualRevenue, occurredAt: e.target.value })}
                    style={{
                      padding: '12px 14px', borderRadius: '8px',
                      border: '2px solid var(--border-color)', background: 'var(--bg-tertiary)',
                      color: 'var(--text-primary)', fontSize: '14px', fontFamily: 'inherit',
                      outline: 'none'
                    }}
                  />
                  <div style={{ display: 'flex', gap: '10px' }}>
                    <button
                      onClick={() => setShowManualForm(false)}
                      style={{
                        flex: 1, padding: '10px', borderRadius: '8px',
                        border: '1px solid var(--border-color)', background: 'transparent',
                        color: 'var(--text-secondary)', fontSize: '13px', cursor: 'pointer',
                        fontFamily: 'inherit'
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleManualSubmit}
                      style={{
                        flex: 1, padding: '10px', borderRadius: '8px', border: 'none',
                        background: 'var(--accent-gradient)', color: 'white',
                        fontSize: '13px', fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit'
                      }}
                    >
                      Save
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Info Box */}
            <div style={{
              padding: '16px', borderRadius: '10px', marginBottom: '20px',
              background: 'var(--accent-subtle-bg)', border: '1px solid var(--accent-subtle-border)'
            }}>
              <p style={{ fontSize: '13px', color: 'var(--text-secondary)', lineHeight: '1.5' }}>
                💡 <strong style={{ color: 'var(--text-primary)' }}>How it works:</strong> When connected, we automatically sync your sales and attribute them to your posts based on timing and UTM parameters. This helps you see which content drives the most revenue.
              </p>
            </div>

            {/* Close Button */}
            <button
              onClick={onClose}
              style={{
                width: '100%', padding: '14px', borderRadius: '10px',
                border: '2px solid var(--border-color)', background: 'transparent',
                color: 'var(--text-secondary)', fontSize: '14px', cursor: 'pointer', fontFamily: 'inherit'
              }}
            >
              Close
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default RevenueTracker;
